/**
 * First-sign-in migration: move everything from the local workspace into
 * the user's cloud workspace, then clear local storage.
 *
 * CRITICAL: note content moves as CRDT STATE (encodeStateAsUpdate ->
 * applyUpdate into the fresh cloud doc), never via markdown re-seeding.
 * The cloud doc has a different doc name, so this is a one-time seed of a
 * brand-new doc — the safe kind.
 */
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import { DOC_KEYS } from "@hydra-notes/shared";
import { toast } from "@/ui/toast";
import { LOCAL_WORKSPACE_ID, WorkspaceStore } from "./workspace";
import { useSyncState } from "./sync";
import type { DataContext } from "./store";

const migratedFlag = (wsId: string) => `hn.migrated.${wsId}`;

export async function migrateLocalToCloud(cloud: DataContext): Promise<void> {
  if (localStorage.getItem(migratedFlag(cloud.workspaceId))) return;

  // Peek at the local workspace without mounting it as the active context.
  const local = new WorkspaceStore(LOCAL_WORKSPACE_ID);
  await local.whenLoaded;
  const items = local.getSnapshot();
  if (items.length === 0) {
    local.destroy();
    localStorage.setItem(migratedFlag(cloud.workspaceId), new Date().toISOString());
    return;
  }

  await cloud.workspace.whenLoaded;

  // 1) copy tree items (skip ids that already exist in the cloud)
  let copied = 0;
  cloud.workspace.doc.transact(() => {
    for (const item of items) {
      if (cloud.workspace.items.has(item.id)) continue;
      const m = new Y.Map<unknown>();
      m.set("type", item.type);
      m.set("parentId", item.parentId);
      m.set("title", item.title);
      m.set("sortKey", item.sortKey);
      m.set("tags", item.tags);
      m.set("createdAt", item.createdAt);
      m.set("updatedAt", item.updatedAt);
      if (item.folderNoteId) m.set("folderNoteId", item.folderNoteId);
      if (item.trashedAt) m.set("trashedAt", item.trashedAt);
      if (item.archivedAt) m.set("archivedAt", item.archivedAt);
      if (item.pinned) m.set("pinned", true);
      if (item.aliases) m.set("aliases", item.aliases);
      // migration is the same user's data moving home: unlike cross-space
      // transfers, builtin and colorKey come along, or the cloud boot seeds
      // a DUPLICATE default Tasks list next to the migrated one
      if (item.colorKey) m.set("colorKey", item.colorKey);
      if (item.builtin) m.set("builtin", item.builtin);
      cloud.workspace.items.set(item.id, m);
      copied++;
    }
  });

  // 2) copy note contents as CRDT state
  // Cards use the same per-item note doc for their description and media.
  const noteIds = items.filter((i) => i.type === "note" || i.type === "card").map((i) => i.id);
  for (const noteId of noteIds) {
    const srcDoc = new Y.Doc({ gc: true });
    const srcPersistence = new IndexeddbPersistence(`hn.note.${LOCAL_WORKSPACE_ID}.${noteId}`, srcDoc);
    await srcPersistence.whenSynced;
    const hasContent = srcDoc.getText(DOC_KEYS.noteContent).length > 0;
    if (hasContent) {
      const state = Y.encodeStateAsUpdate(srcDoc);
      const handle = cloud.notes.open(noteId);
      await handle.whenLoaded;
      Y.applyUpdate(handle.doc, state, "migration");
      handle.release();
    }
    srcPersistence.destroy();
    srcDoc.destroy();
  }

  // 2b) task-list docs the same way (a local anonymous workspace has no
  //     server, so its tasks never carried seq; nothing to strip)
  const taskListIds = items.filter((i) => i.type === "tasklist").map((i) => i.id);
  for (const listId of taskListIds) {
    const srcDoc = new Y.Doc({ gc: true });
    const srcPersistence = new IndexeddbPersistence(`hn.task.${LOCAL_WORKSPACE_ID}.${listId}`, srcDoc);
    await srcPersistence.whenSynced;
    if (srcDoc.getMap(DOC_KEYS.taskItems).size > 0) {
      const state = Y.encodeStateAsUpdate(srcDoc);
      const handle = cloud.tasks.open(listId);
      await handle.whenLoaded;
      Y.applyUpdate(handle.doc, state, "migration");
      handle.release();
    }
    srcPersistence.destroy();
    srcDoc.destroy();
  }

  // 3) wait until the sync engine reports everything acknowledged
  const uploaded = await waitForSynced(120_000);

  if (uploaded) {
    localStorage.setItem(migratedFlag(cloud.workspaceId), new Date().toISOString());
    await wipeLocalWorkspace(local, noteIds, taskListIds);
    toast.success(`${copied} item${copied === 1 ? "" : "s"} backed up to your account`);
  } else {
    // keep local data; migration will retry next launch
    toast.error("Backup upload is taking long — your notes are safe locally and upload will resume.");
  }
  local.destroy();
}

function waitForSynced(timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now();
    const timer = setInterval(() => {
      const st = useSyncState.getState();
      if (st.status === "synced" && st.dirtyDocs === 0) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 500);
  });
}

async function wipeLocalWorkspace(local: WorkspaceStore, noteIds: string[], taskListIds: string[] = []): Promise<void> {
  // the local workspace always persists; only shared spaces skip IndexedDB
  await local.persistence?.clearData();
  const dbNames = [
    ...noteIds.map((id) => `hn.note.${LOCAL_WORKSPACE_ID}.${id}`),
    ...taskListIds.map((id) => `hn.task.${LOCAL_WORKSPACE_ID}.${id}`),
  ];
  for (const name of dbNames) {
    await new Promise<void>((resolve) => {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = req.onerror = req.onblocked = () => resolve();
    });
  }
}
